import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { canApproveReconResolution, canResolveReconException } from '@/domain/permissions';
import { currentActor } from '@/domain/actor';
import type { Persona } from '@/domain/types';
import type { DepositReconciliation, DepositReconState } from '@/domain/depositReconFlow';

/**
 * Reconcile exception resolutions (Prompt K, K1). The two reconciliation lanes
 * derive their rows read-only; this module adds the ACTIONS on top: an exception
 * is investigated and Resolved with a required note (+ optional mock attachment),
 * which parks it in an "Awaiting approval" group until a DIFFERENT, cleared person
 * approves it into Matched — self-approval is blocked. The resolution record is the
 * audit trail (who resolved, who approved, when). Nothing here re-runs the recon
 * derivations; it only overlays their rows by row id.
 *
 * Also here: the partial-deposit chain (a deposit paid in parts) - derived purely
 * from the ledger shortfall the deposit lane already carries, never a stored flag.
 */

export type ReconLane = 'makesale' | 'deposit';
export type ResolutionStatus = 'suggested-resolved' | 'approved' | 'rejected';

/** A mock attachment on a resolution - filename + a display size, no real upload. */
export interface ReconAttachment {
  filename: string;
  sizeLabel: string;
}

export interface ReconResolution {
  /** The reconciliation row this resolves - its stable row id (keys the store). */
  rowId: string;
  lane: ReconLane;
  /** Human reference for the trail (e.g. DEP-CBG-3820). */
  reference: string;
  status: ResolutionStatus;
  note: string;
  attachment?: ReconAttachment;
  resolvedById: string;
  resolvedByName: string;
  resolvedAt: string;
  approvedById?: string;
  approvedByName?: string;
  approvedAt?: string;
  /** L4 (17 Jul): why an approver bounced the resolution; shown back in Needs you. */
  rejectionNote?: string;
}

function nowIso(): string {
  return useSimulationStore.getState().clockIso;
}

export interface ResolveInput {
  rowId: string;
  lane: ReconLane;
  reference: string;
  note: string;
  attachment?: ReconAttachment;
}

/**
 * Resolve an exception row. A note is required - an empty note stores nothing.
 * Only Accounts resolves (W7 n3, `canResolveReconException`) - anyone else stores
 * nothing. Parks the row in "Awaiting approval" as `suggested-resolved`, stamped
 * with the resolver (a real person, e.g. "Robin Ashby") and time.
 */
export function resolveReconRow(input: ResolveInput): ReconResolution | undefined {
  const note = input.note.trim();
  if (!note) return undefined;
  const actor = currentActor();
  if (!actor.persona || !canResolveReconException(actor.persona)) return undefined;
  const resolution: ReconResolution = {
    rowId: input.rowId,
    lane: input.lane,
    reference: input.reference,
    status: 'suggested-resolved',
    note,
    ...(input.attachment ? { attachment: input.attachment } : {}),
    resolvedById: actor.id,
    resolvedByName: actor.name,
    resolvedAt: nowIso(),
  };
  useBrainStore.getState().setReconResolution(resolution);
  return resolution;
}

export type ApprovalBlockReason = 'no-clearance' | 'self-approval';

/**
 * Whether `persona`/`actorId` may approve `resolution`: Finance Manager clearance
 * AND a different person than the resolver (maker-checker, diagram 11). Drives the
 * Approve button's enabled state and its blocked-reason copy. tv-vhs: the
 * clearance keys to the Finance Manager, not the reused Ops no-deposit override.
 */
export function reconApprovalBlock(
  persona: Persona | null,
  resolution: ReconResolution,
  actorId: string,
): { canApprove: true } | { canApprove: false; reason: ApprovalBlockReason } {
  // Self-approval is the salient block for whoever resolved the row (even if they
  // also lack clearance) - that is the message the resolver should see.
  if (actorId === resolution.resolvedById) {
    return { canApprove: false, reason: 'self-approval' };
  }
  if (!persona || !canApproveReconResolution(persona)) {
    return { canApprove: false, reason: 'no-clearance' };
  }
  return { canApprove: true };
}

export type ApproveResult = { ok: true } | { ok: false; reason: ApprovalBlockReason | 'not-pending' };

/** Approve a suggested-resolved row into Matched, if the actor is cleared and different. */
export function approveReconResolution(rowId: string): ApproveResult {
  const brain = useBrainStore.getState();
  const res = brain.reconResolutions[rowId];
  if (!res || res.status !== 'suggested-resolved') return { ok: false, reason: 'not-pending' };
  const actor = currentActor();
  const block = reconApprovalBlock(actor.persona, res, actor.id);
  if (!block.canApprove) return { ok: false, reason: block.reason };
  brain.setReconResolution({
    ...res,
    status: 'approved',
    approvedById: actor.id,
    approvedByName: actor.name,
    approvedAt: nowIso(),
  });
  return { ok: true };
}

export type RejectResult = { ok: true } | { ok: false; reason: 'no-clearance' | 'no-reason' | 'not-pending' };

/**
 * Reject a suggested-resolved row back to Needs you. Only the Finance Manager
 * rejects (W7 n3 - the checker bounces, the maker never un-parks their own), and
 * a reason is required (L4, 17 Jul): it is stored on the resolution so the
 * resolver sees why it bounced.
 */
export function rejectReconResolution(rowId: string, note: string): RejectResult {
  const actor = currentActor();
  if (!actor.persona || !canApproveReconResolution(actor.persona)) return { ok: false, reason: 'no-clearance' };
  const trimmed = note.trim();
  if (!trimmed) return { ok: false, reason: 'no-reason' };
  const brain = useBrainStore.getState();
  const res = brain.reconResolutions[rowId];
  if (!res || res.status !== 'suggested-resolved') return { ok: false, reason: 'not-pending' };
  brain.setReconResolution({ ...res, status: 'rejected', rejectionNote: trimmed });
  return { ok: true };
}

export type RowClass = 'needs' | 'awaiting' | 'approved';

/** Where a resolvable row sits given its resolution (if any). */
export function classifyResolvedRow(
  rowId: string,
  resolutions: Record<string, ReconResolution>,
): RowClass {
  const res = resolutions[rowId];
  if (!res) return 'needs';
  if (res.status === 'suggested-resolved') return 'awaiting';
  if (res.status === 'approved') return 'approved';
  return 'needs'; // rejected → back in the exceptions
}

// --- Partial-deposit chain -------------------------------------------------

export interface PartialMovement {
  movementKind: 'paid' | 'topup';
  movedAud: number;
  dayIso: string;
  state: DepositReconState;
  landedOn?: string;
}

export interface PartialTopUp {
  reference: string;
  name: string;
  /** Agreed/sent amount on the deposit. */
  sentAud: number;
  /** What has actually moved so far (sum of the movement rows). */
  paidAud: number;
  /** Still to come (the ledger shortfall). */
  outstandingAud: number;
  movements: PartialMovement[];
}

/**
 * Deposits paid in parts, from a reconciliation's rows: any deposit whose movements
 * carry a shortfall (sent > paid). Purely derived - `shortfallAud` is the ledger
 * maths the deposit lane already computes, never a stored "partial" flag. Orphan
 * bank landings (no system side) are skipped. Each entry lists its movement chain
 * so "part two of whatever came through" reads as one story.
 */
export function partialTopUps(recon: DepositReconciliation): PartialTopUp[] {
  const byRef = new Map<string, PartialTopUp>();
  for (const row of recon.rows) {
    if (row.state === 'in-bank-not-system') continue; // orphan landing, no deposit
    if (row.shortfallAud <= 0) continue;
    const existing = byRef.get(row.reference);
    const movement: PartialMovement = {
      movementKind: row.movementKind,
      movedAud: row.movedAud,
      dayIso: row.dayIso,
      state: row.state,
      ...(row.landedOn ? { landedOn: row.landedOn } : {}),
    };
    if (existing) {
      existing.movements.push(movement);
      existing.paidAud += row.movedAud;
    } else {
      byRef.set(row.reference, {
        reference: row.reference,
        name: row.name,
        sentAud: row.sentAud,
        paidAud: row.movedAud,
        outstandingAud: row.shortfallAud,
        movements: [movement],
      });
    }
  }
  return [...byRef.values()];
}
